/**
 * QuickReplyPicker — popover that lists the caller's SMS quick-reply
 * templates (own + system) and inserts the chosen template body into the
 * composer. Fetches lazily on open via smsAPI.getQuickReplies; offers a
 * one-click "add default templates" seed when the list is empty.
 *
 * Props
 * -----
 *   onPick(message) — called with the chosen template's message text.
 *   testId          — data-testid prefix. Defaults to "quick-reply".
 */
import { useEffect, useRef, useState } from "react";
import { MessageSquareText, Plus } from "lucide-react";
import { smsAPI, smsTemplatesAPI } from "../../services/api";
import { toast } from "sonner";

export default function QuickReplyPicker({ onPick, testId = "quick-reply" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const rootRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [qr, tpl] = await Promise.allSettled([smsAPI.getQuickReplies(), smsTemplatesAPI.list()]);
      setItems(qr.status === "fulfilled" ? (qr.value.data || []) : []);
      setTemplates(tpl.status === "fulfilled" ? (tpl.value.data?.templates || []) : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const seed = async () => {
    setSeeding(true);
    try { await smsAPI.seedDefaultReplies(); await load(); toast.success("Added default templates"); }
    catch { toast.error("Couldn't add default templates"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors"
        title="Quick replies"
        data-testid={`${testId}-toggle`}
      >
        <MessageSquareText className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute bottom-full mb-2 right-0 z-50 w-[300px] max-h-[320px] overflow-y-auto rounded-lg border bg-white shadow-lg p-2"
          data-testid={`${testId}-popover`}
        >
          {loading && <div className="text-[12px] text-slate-400 p-2">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-3 text-center">
              <div className="text-[12px] text-slate-500 mb-2">No quick replies yet.</div>
              <button
                onClick={seed}
                disabled={seeding}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#032866] disabled:opacity-50"
                data-testid={`${testId}-seed`}
              >
                <Plus className="w-3 h-3" /> {seeding ? "Adding…" : "Add default templates"}
              </button>
            </div>
          )}
          {!loading && items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { onPick(t.message || ""); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100"
              data-testid={`${testId}-item-${t.id}`}
            >
              <div className="text-[12px] font-semibold text-slate-800 truncate">{t.title}</div>
              <div className="text-[11px] text-slate-500 line-clamp-2">{t.message}</div>
            </button>
          ))}
          {!loading && templates.length > 0 && (
            <div className="mt-1 pt-1" style={{ borderTop: "1px solid rgba(3,40,102,0.08)" }}>
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Templates</div>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onPick(t.body || ""); setOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100"
                  data-testid={`${testId}-template-${t.id}`}
                >
                  <div className="text-[12px] font-semibold text-slate-800 truncate">{t.title}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2">{t.body}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
